/**
 * Geometry for the shelf mask editor. Pure functions, no React — so the arithmetic that
 * decides a stored label can be tested without a browser.
 *
 * Coordinates are the UPSCALED crop's pixels, the same frame the backend stored contours
 * in. Nothing here converts to screen space: the SVG does that with a viewBox, which
 * keeps one coordinate system in play instead of two.
 */
/**
 * Fill a polygon into a bitmap, in plain arithmetic.
 *
 * A canvas would do this — and did — but a canvas needs a DOM, and jsdom has none, so
 * seven tests over the logic that decides a stored label were silently skipping in CI.
 * Scanline fill is thirty lines and runs everywhere, which is worth more here than
 * whatever the GPU would have saved on masks this small.
 *
 * Even-odd rule, half-open pixel centres: a point exactly on an edge belongs to one
 * polygon and not both, so adjacent objects do not double-count their shared border.
 */
export function fillPolygon(
  out: Uint8Array, poly: Contour, w: number, h: number, scale = 1,
): void {
  if (poly.length < 3) return;
  const xs = poly.map(p => p[0] * scale);
  const ys = poly.map(p => p[1] * scale);
  const yMin = Math.max(0, Math.floor(Math.min(...ys)));
  const yMax = Math.min(h - 1, Math.ceil(Math.max(...ys)));
  const nodes: number[] = [];
  for (let y = yMin; y <= yMax; y++) {
    const cy = y + 0.5;
    nodes.length = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const yi = ys[i];
      const yj = ys[j];
      if ((yi > cy) === (yj > cy)) continue;
      nodes.push(xs[i] + ((cy - yi) / (yj - yi)) * (xs[j] - xs[i]));
    }
    nodes.sort((a, b) => a - b);
    for (let k = 0; k + 1 < nodes.length; k += 2) {
      const x0 = Math.max(0, Math.ceil(nodes[k] - 0.5));
      const x1 = Math.min(w - 1, Math.floor(nodes[k + 1] - 0.5));
      const row = y * w;
      for (let x = x0; x <= x1; x++) out[row + x] = 1;
    }
  }
}

/** Bitmap of one contour at a working scale, plus how many pixels it covers. */
function rasterise(c: Contour, w: number, h: number, scale: number): [Uint8Array, number] {
  const bmp = new Uint8Array(w * h);
  fillPolygon(bmp, c, w, h, scale);
  let n = 0;
  for (let i = 0; i < bmp.length; i++) n += bmp[i];
  return [bmp, n];
}


/** Fewer points than this is a line, not a shape, and cannot enclose any area. */
export const MIN_POINTS = 3;

export type Point = [number, number];
export type Contour = Point[];

/** Objects from every prompt, largest first, each tagged with where it came from.
 *
 *  The prompts NEST by area — measured on live crops, `packaging` sits 89% inside
 *  `groceries`, which sits 97% inside `bottle` — so ordering every object by size gives
 *  a ladder from "the biggest single thing" down to "every crumb", and walking it is a
 *  continuous sweep a human can stop at the right place. */
export interface LadderItem {
  contour: Contour;
  area: number;
  prompt: string;
}

/** No size cut by default, and that is a measurement rather than an omission.
 *
 *  A cut at 25% looked obviously right — `groceries` on a full shelf returns ONE mask
 *  covering 97% of it, which is useless as an object. But greedy cover from the smallest
 *  already discards those: by the time the blob is considered, the fine objects have
 *  covered it. Measured over 100 real crops, the cut changed the object count not at all
 *  (median 16 either way) and cost real area — coverage lost went from 3.9% to 6.3% at
 *  the median and from 9.4% to 48.1% at p90, because on a genuinely full shelf the big
 *  mask is the honest answer.
 *
 *  Kept as a parameter because a future prompt might need it; defaulted off because this
 *  one does not. */
export const MAX_OBJECT_FRAC = 1;
/** An object this covered by ones already kept is the same goods found again under
 *  another prompt. Measured on 12 real crops: of 3124 overlapping pairs, 2031 were one
 *  object sitting >85% inside another. */
export const DUPLICATE_OVERLAP = 0.85;

/**
 * Drop duplicates and blobs, keeping the FINE-GRAINED objects.
 *
 * Smallest first, deliberately. Greedy cover from the largest would keep the 97% blob and
 * then discard every bottle inside it — technically the same coverage, and useless to a
 * labeller, who can neither delete one wrong bottle nor drag one edge. Starting small
 * keeps the individually-editable objects and lets the coarse restatements fall away.
 *
 * Coverage is tested on a coarse raster: this runs over up to two hundred objects and the
 * question is "is this mostly already covered", which does not need pixel precision.
 */
export function dedupeObjects(
  items: LadderItem[], width: number, height: number,
  overlapMax: number = DUPLICATE_OVERLAP, maxFrac: number = MAX_OBJECT_FRAC,
): LadderItem[] {
  if (!items.length || width <= 0 || height <= 0) return [];
  const SCALE = Math.min(1, 400 / Math.max(width, height));
  const w = Math.max(1, Math.round(width * SCALE));
  const h = Math.max(1, Math.round(height * SCALE));
  const covered = new Uint8Array(w * h);
  const kept: LadderItem[] = [];
  const small = [...items]
    .filter(i => i.area / 100 <= maxFrac)
    .sort((a, b) => a.area - b.area);

  for (const item of small) {
    const [bmp, own] = rasterise(item.contour, w, h, SCALE);
    if (!own) continue;
    let already = 0;
    for (let i = 0; i < bmp.length; i++) if (bmp[i] && covered[i]) already++;
    if (already / own > overlapMax) continue;      // the same goods, found again
    for (let i = 0; i < bmp.length; i++) if (bmp[i]) covered[i] = 1;
    kept.push(item);
  }
  // Largest first on the way out: the slider adds coverage as it moves right.
  return kept.sort((a, b) => b.area - a.area);
}

export function buildLadder(
  prompts: { prompt: string; contours: number[][][]; areas: number[] }[],
): LadderItem[] {
  const out: LadderItem[] = [];
  for (const p of prompts) {
    p.contours.forEach((c, i) => {
      if (c && c.length >= 3) {
        out.push({ contour: c as Contour, area: p.areas?.[i] ?? 0, prompt: p.prompt });
      }
    });
  }
  // Largest first: the slider adds detail as it moves right, which reads the same way
  // round as "more of the shelf is covered".
  return out.sort((a, b) => b.area - a.area);
}

/** The first `n` rungs of the ladder — what the slider hands to the editor. */
export function ladderSlice(ladder: LadderItem[], n: number): Contour[] {
  return ladder.slice(0, Math.max(0, Math.min(n, ladder.length))).map(i => i.contour);
}

/**
 * Share of the frame the contours cover, overlap counted ONCE.
 *
 * Rasterised on a canvas rather than summed, for the same reason the server rasterises:
 * two prompts routinely find the same bottle, and adding their areas reports 14% for one
 * 7% object. Downscaled to a working canvas because this runs on every drag frame and
 * counting five million pixels at 60fps is not a thing.
 *
 * The server recomputes this on save at full resolution. This is for the number that
 * moves under the cursor, not for the number that gets stored.
 */
export function coverageOf(contours: Contour[], width: number, height: number): number {
  if (!contours.length || width <= 0 || height <= 0) return 0;
  const MAX = 600;
  const scale = Math.min(1, MAX / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  const canvas = new Uint8Array(w * h);
  for (const c of contours) {
    if (c && c.length >= MIN_POINTS) fillPolygon(canvas, c, w, h, scale);
  }
  let on = 0;
  for (let i = 0; i < canvas.length; i++) on += canvas[i];
  return Math.round((1000 * on) / (w * h)) / 10;
}

/** Index of the contour under a point, topmost (last drawn) first, or -1. */
export function hitTest(contours: Contour[], x: number, y: number): number {
  for (let i = contours.length - 1; i >= 0; i--) {
    if (pointInPolygon(contours[i], x, y)) return i;
  }
  return -1;
}

export function pointInPolygon(poly: Contour, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const crosses = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Nearest vertex to a point, if it is within `radius`. Used to grab a handle. */
export function nearestVertex(
  poly: Contour, x: number, y: number, radius: number,
): number {
  let best = -1;
  let bestD = radius * radius;
  poly.forEach(([px, py], i) => {
    const d = (px - x) ** 2 + (py - y) ** 2;
    if (d <= bestD) { bestD = d; best = i; }
  });
  return best;
}


/** One physical object, however many prompts found it. */
export interface MergedObject {
  /** The representative outline — the largest contour in the cluster, because it is the
   *  one a labeller can actually grab and drag. */
  contour: Contour;
  area: number;
  /** Every prompt that found this object. Two or more means two independent words agreed
   *  it is there, which is what makes it BASE rather than a guess. */
  prompts: string[];
  /** Every outline that went into it, largest first. Merging is a decision about what to
   *  SHOW, not about throwing detections away: a broad outline that swallowed a bun the
   *  labeller wants gone can be split back into these, and then the bun is its own
   *  deletable object instead of a corner of something too big to edit. */
  members: Contour[];
}

/**
 * Group overlapping detections into objects.
 *
 * `food`, `packaging` and `groceries` routinely return the same bottle — measured, 65% of
 * overlapping pairs were one contour sitting >85% inside another. Treating those as three
 * objects is what made a crop show 30 of them. Grouped, they are one object that three
 * prompts agreed on.
 *
 * Agreement is the useful signal here: an object several prompts found independently is
 * almost certainly real, and one only a single prompt found is a judgement call. So the
 * caller can split them — base against per-prompt extras — instead of drowning in both.
 *
 * Merge when the overlap is more than a quarter OF THE SMALLER object. Deliberately
 * generous: what makes the screen unreadable is one coarse outline lying across several
 * fine ones, and those share little of their COMBINED area — intersection over union
 * leaves them all on screen however low it is set. Measured over a hundred real crops,
 * IoU 0.5 leaves a median of 16 objects, IoU 0.25 leaves 13, this leaves 4.
 *
 * The obvious objection already bit once: merging a shelf-wide mask with the bottles
 * inside it reported a full shelf as 0.1% covered. That was the REPRESENTATIVE — the
 * smallest member was kept. The largest is kept now, and base coverage rises under this
 * rule rather than collapsing, 43.8% to 52.0% at the median. The real cost is that
 * objects get coarser, so editing one bottle out of a merged group means the pen rather
 * than dragging a corner.
 */
export function clusterObjects(
  items: LadderItem[], width: number, height: number, overlapMin: number = 0.25,
): MergedObject[] {
  if (!items.length || width <= 0 || height <= 0) return [];
  const SCALE = Math.min(1, 400 / Math.max(width, height));
  const w = Math.max(1, Math.round(width * SCALE));
  const h = Math.max(1, Math.round(height * SCALE));

  // Largest first so a cluster is anchored on its biggest member.
  const sorted = [...items].sort((a, b) => b.area - a.area);
  const rasters = sorted.map(i => rasterise(i.contour, w, h, SCALE));

  const clusters: { idx: number[]; mask: Uint8Array }[] = [];
  sorted.forEach((_item, i) => {
    const [mi] = rasters[i];
    let joined = -1;
    for (let c = 0; c < clusters.length; c++) {
      const cm = clusters[c].mask;
      let inter = 0;
      let own = 0;
      let theirs = 0;
      for (let p = 0; p < mi.length; p++) {
        if (mi[p]) own++;
        if (cm[p]) theirs++;
        if (mi[p] && cm[p]) inter++;
      }
      const smaller = Math.max(1, Math.min(own, theirs));
      if (inter && inter / smaller > overlapMin) { joined = c; break; }
    }
    if (joined < 0) {
      clusters.push({ idx: [i], mask: Uint8Array.from(mi) });
    } else {
      clusters[joined].idx.push(i);
      const cm = clusters[joined].mask;
      for (let p = 0; p < mi.length; p++) if (mi[p]) cm[p] = 1;
    }
  });

  return clusters.map(c => {
    const members = c.idx.map(i => sorted[i]);
    const prompts = [...new Set(members.map(m => m.prompt))];
    // Largest member: under IoU the members are the SAME object outlined by different
    // words, so the fullest outline is the one worth keeping. Taking the smallest is what
    // reported a full shelf as 0.1% covered.
    const rep = members.reduce((a, b) => (a.area >= b.area ? a : b));
    return {
      contour: rep.contour,
      area: rep.area,
      prompts,
      members: [...members].sort((a, b) => b.area - a.area).map(m => m.contour),
    };
  }).sort((a, b) => b.area - a.area);
}

/** Objects several prompts agreed on — the set the slider works over by default.
 *
 *  When NOTHING agreed, fall back to the single prompt that found the most. Measured
 *  2026-09-13: dessert and pastry racks answer to `food` and to nothing else, so 17% of
 *  the pre-labelled crops had an empty base and opened at 0% on a stocked shelf. One
 *  prompt is weaker evidence than two, but it is evidence. Mirrors `base_objects` in
 *  sam_masks.py — the editor and the stored percentage must come from the same rule. */
export function baseObjects(
  objs: MergedObject[], width = 0, height = 0,
): MergedObject[] {
  const agreed = objs.filter(o => o.prompts.length > 1);
  if (agreed.length || !objs.length) return agreed;
  const byPrompt = new Map<string, MergedObject[]>();
  for (const o of objs) {
    for (const p of o.prompts) byPrompt.set(p, [...(byPrompt.get(p) ?? []), o]);
  }
  // Without dimensions there is no rasterised coverage to compare, so rank by summed
  // area instead: same ordering whenever one prompt's finds don't overlap each other,
  // which is the case here — they came out of one SAM run as distinct instances.
  const score = (os: MergedObject[]) => (width > 0 && height > 0
    ? coverageOf(os.map(o => o.contour), width, height)
    : os.reduce((s, o) => s + o.area, 0));
  return [...byPrompt.values()].reduce((a, b) => (score(a) >= score(b) ? a : b));
}

/** Objects only this prompt found — what ticking its box adds. */
export const uniqueTo = (objs: MergedObject[], prompt: string): MergedObject[] =>
  objs.filter(o => o.prompts.length === 1 && o.prompts[0] === prompt);
